import LocalStorage from 'web-app/services/local-storage';
import {
    CUSTOM_LOCAL_STORAGE_EVENT,
    TRACKING_EVENTS_LOCAL_STORAGE_KEY,
} from 'web-app/react/tracking-visualization/constants';
import {type Event} from 'web-app/util/famlytics/types';
import {hasValue} from 'web-app/util/typescript';

const findMatchingObject = <T extends Record<string, any>>(event: T, input: string) =>
    Object.entries(event).find(([key, value]) => {
        if (key.toLowerCase().includes(input.toLowerCase())) {
            return true;
        }
        if (typeof value === 'object') {
            return Boolean(findMatchingObject(value, input));
        }
        return false;
    });

export const getIsObjectKeySameAsInput = <T extends Record<string, any>>(event: T, input: string) => {
    const matchingObject = findMatchingObject(event, input);
    if (matchingObject === undefined) {
        return false;
    }
    if (matchingObject.length > 0) {
        return true;
    }
    return false;
};

export const sanitizeJsonString = (jsonString: string) =>
    jsonString
        // Remove these characters: { } " ,
        // It's more readable this way by non-engineers
        .replaceAll(/{|}|"|,/g, '')
        // Remove all empty lines
        // This way the spacing is much nicer
        .replaceAll(/^\s*$(?:\r\n?|\n)/gm, '');

export const saveEventInLocalStorage = (event: Event) => {
    const currentEventsInLocalStorage = LocalStorage.getFromLocalStorage(TRACKING_EVENTS_LOCAL_STORAGE_KEY);
    if (!hasValue(currentEventsInLocalStorage)) {
        LocalStorage.saveToLocalStorage(TRACKING_EVENTS_LOCAL_STORAGE_KEY, [event]);
        return;
    }

    LocalStorage.saveToLocalStorage(TRACKING_EVENTS_LOCAL_STORAGE_KEY, [...currentEventsInLocalStorage, event]);
    window.dispatchEvent(new CustomEvent(CUSTOM_LOCAL_STORAGE_EVENT));
};

export const hasAttribute = (element: HTMLElement, dataAttribute: string) =>
    element.getAttribute(dataAttribute) !== null;

export const getSafeZoneForYAxis = (
    coordinate: number | null,
    cutOffZone: number | null,
    popupHeight: number | null,
) => {
    // We need to add extra 50 pixel to the popup height, so we make sure to render it without getting cut off
    const adjustedPopupDimension = popupHeight ? popupHeight + 50 : null;

    // If the coordinate is 0, we need to give a default value so the popup has some white space
    if (!cutOffZone && coordinate === 0) {
        return 40;
    }

    // We don't need to set a safe zone if we don't have a cut off zone or coordinates
    if (!cutOffZone || !coordinate) {
        return null;
    }

    // By default we render the popup to the bottom-right side of the corresponding element
    // If our cut off zone is less or equal than our popup dimension (width or height) it means that the popup would get cut off
    if (adjustedPopupDimension && cutOffZone <= adjustedPopupDimension) {
        return coordinate - adjustedPopupDimension + 40;
    }

    if (!(adjustedPopupDimension && cutOffZone <= adjustedPopupDimension)) {
        return coordinate + 50;
    }

    return null;
};

export const getSafeZoneForXAxis = (
    coordinate: number | null,
    cutOffZone: number | null,
    popupWidth: number | null,
) => {
    // If the coordinate is 0, it means it's touching the viewport's side so we need to give a default value for the popup to have some white space
    if (!cutOffZone && coordinate === 0) {
        return 40;
    }

    // We don't need to set a safe zone if we don't have a cut off zone or coordinates
    if (!cutOffZone || !coordinate) {
        return null;
    }

    // By default we render the popup to the bottom-right side of the corresponding element
    // If our cut off zone is less or equal than our popup dimension (width or height) it means that the popup would get cut off.
    if (popupWidth && cutOffZone <= popupWidth) {
        return coordinate - popupWidth - 20;
    }

    if (!(popupWidth && cutOffZone <= popupWidth)) {
        return coordinate + 50;
    }

    return null;
};

export const getCutOffZone = (viewPortDimension: number | null, coordinate: number | null) =>
    viewPortDimension && coordinate ? viewPortDimension - coordinate : null;
